import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plane, LogOut, CreditCard } from "lucide-react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

/**
 * Self-service fleet (Aerofleet Center) account chrome:
 *  - FleetPaywall: full-page lock when the trial ended unpaid / subscription cancelled
 *  - FleetNotices: first-login "name your organisation" dialog, trial / payment banners
 * Schools without `billing` (all pre-existing ones) never see any of this.
 */

export const isLocked = (school) =>
  school?.product === "fleet" && school?.billing?.managed && !school?.billing?.entitled;

const eur = (n) => `${n.toLocaleString("pt-PT", { minimumFractionDigits: 0 })} €`;

function useBillingInfo(enabled) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    axios
      .get(`${API}/fleet/billing`, { withCredentials: true })
      .then((r) => setInfo(r.data))
      .catch(() => {});
  }, [enabled]);
  return info;
}

// Stripe Checkout / customer portal: backend returns a URL to send the browser to.
function useBillingRedirect() {
  const [busy, setBusy] = useState(false);
  const go = async (path) => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/fleet/billing/${path}`,
        { origin_url: window.location.origin },
        { withCredentials: true },
      );
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível abrir o pagamento");
      setBusy(false);
    }
  };
  return { busy, go };
}

function PriceLine({ info }) {
  if (!info) return null;
  return (
    <p className="text-xs text-slate-400">
      {eur(info.price_per_aircraft_eur)} por aeronave / mês. Atualmente {info.aircraft_count}{" "}
      {info.aircraft_count === 1 ? "aeronave" : "aeronaves"}: {eur(info.monthly_total_eur)} / mês.
    </p>
  );
}

export function FleetPaywall({ school, onLogout }) {
  const info = useBillingInfo(true);
  const { busy, go } = useBillingRedirect();
  const cancelled = school?.billing?.status === "canceled";
  return (
    <div className="min-h-screen bg-cockpit-bg flex items-center justify-center px-6" data-testid="fleet-paywall">
      <div className="max-w-md w-full instrument-panel text-center space-y-4">
        <div className="w-12 h-12 mx-auto bg-orange-500 rounded-lg flex items-center justify-center">
          <Plane className="w-6 h-6 text-white" />
        </div>
        <h1 className="font-chivo text-xl text-cockpit-text-bright">
          {cancelled ? "A subscrição foi cancelada" : "O período de teste terminou"}
        </h1>
        <p className="text-sm text-cockpit-text">
          O acesso de <span className="text-cockpit-text-bright">{school?.name}</span> ao Aerofleet Center está
          bloqueado até ser ativada uma subscrição. Os seus dados ficam guardados.
        </p>
        <PriceLine info={info} />
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button onClick={() => go("checkout")} disabled={busy} className="btn-cockpit" data-testid="subscribe-btn">
            <CreditCard className="w-4 h-4 mr-2" /> {busy ? "A abrir..." : "Subscrever"}
          </Button>
          <Button variant="outline" onClick={onLogout} className="btn-secondary-cockpit">
            <LogOut className="w-4 h-4 mr-2" /> Terminar sessão
          </Button>
        </div>
      </div>
    </div>
  );
}

export function FleetNotices({ school, onSaved }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const isFleet = school?.product === "fleet" && school?.billing?.managed;
  const status = school?.billing?.status;
  const info = useBillingInfo(isFleet && status === "trialing");
  const { busy, go } = useBillingRedirect();

  // Back from Stripe: the webhook may take a few seconds to activate the school.
  useEffect(() => {
    if (!isFleet || new URLSearchParams(window.location.search).get("billing") !== "success") return;
    toast.success("Pagamento recebido. A ativar a subscrição…");
    const timers = [2000, 6000, 12000].map((ms) => setTimeout(() => onSaved?.(), ms));
    window.history.replaceState({}, "", window.location.pathname);
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFleet]);

  if (school?.product !== "fleet") return null;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/fleet/school-name`, { name: name.trim() }, { withCredentials: true });
      toast.success("Organização guardada");
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const billing = school.billing;
  return (
    <>
      <Dialog open={!!school.needs_onboarding}>
        <DialogContent
          className="bg-slate-950 border-slate-800"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Bem-vindo ao Aerofleet Center</DialogTitle>
            <DialogDescription>Como se chama a sua organização (escola, aeroclube ou operador)?</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Nome da organização</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Aero Clube de Cascais"
              data-testid="org-name-input"
            />
          </div>
          <DialogFooter>
            <Button
              onClick={save}
              disabled={name.trim().length < 2 || saving}
              className="btn-cockpit"
              data-testid="org-name-save"
            >
              {saving ? "A guardar..." : "Continuar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {billing?.managed && status === "trialing" && (
        <div
          className="mb-6 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg text-sm text-orange-200 flex flex-wrap items-center justify-between gap-3"
          data-testid="trial-banner"
        >
          <div>
            <p>
              Teste grátis: {billing.days_left} {billing.days_left === 1 ? "dia restante" : "dias restantes"}.
            </p>
            <PriceLine info={info} />
          </div>
          <Button size="sm" onClick={() => go("checkout")} disabled={busy} className="btn-cockpit" data-testid="trial-subscribe-btn">
            Subscrever agora
          </Button>
        </div>
      )}

      {billing?.managed && status === "past_due" && (
        <div
          className="mb-6 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-200 flex flex-wrap items-center justify-between gap-3"
          data-testid="past-due-banner"
        >
          <p>O último pagamento falhou. Atualize o método de pagamento para não perder o acesso.</p>
          <Button size="sm" onClick={() => go("portal")} disabled={busy} className="btn-cockpit">
            Atualizar pagamento
          </Button>
        </div>
      )}

      {billing?.managed && status === "active" && (
        <div className="mb-6 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => go("portal")}
            disabled={busy}
            className="btn-secondary-cockpit text-xs"
            data-testid="manage-subscription-btn"
          >
            <CreditCard className="w-3 h-3 mr-1.5" /> Gerir subscrição
          </Button>
        </div>
      )}
    </>
  );
}
